import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { STORAGE_KEYS } from '../config/app.config';
import * as authApi from '../services/auth.service';
import {
  isPlatformSuperAdmin,
  normalizePlatformAdminFlags,
  reconcilePlatformAdminProfile,
  requiresActiveOrganization,
  restorePlatformAdminProfile,
} from '../utils/authAccess';
import * as orgApi from '../services/organizations.service';
import { clearSiteAuthSession, installSiteSyncMessageListener, syncSiteAuthSession } from '../services/siteAuthSync';
import { enrichUserPlatformAccess } from '../utils/enrichUserAccess';
import { mapUserFromApi, mergeAuthUserPayload, userAvatarUrl } from '../utils/mapUser';
import { mergeAuthOrganizations } from '../utils/organization';

const AUTH_BOOTSTRAP_TIMEOUT_MS = 20_000;
const AUTH_BOOTSTRAP_CACHE_TTL_MS = 120_000;
const AUTH_BOOTSTRAP_CACHE_KEY = 'mp_auth_bootstrap_refreshed_at';

function buildOrgContextKey(user, activeOrganization) {
  const orgId = activeOrganization?.id ?? '';
  const perms = Array.isArray(user?.organization_permissions)
    ? user.organization_permissions.join(',')
    : '';
  return `${orgId}:${perms}`;
}

const AuthContext = createContext(null);

function persistUser(user) {
  if (user) window.localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
  else window.localStorage.removeItem(STORAGE_KEYS.USER);
}

function persistBootstrapRefresh(token) {
  if (!token) {
    window.localStorage.removeItem(AUTH_BOOTSTRAP_CACHE_KEY);
    return;
  }
  window.localStorage.setItem(
    AUTH_BOOTSTRAP_CACHE_KEY,
    JSON.stringify({ token, refreshedAt: Date.now() }),
  );
}

function hasFreshBootstrapRefresh(token) {
  try {
    const raw = window.localStorage.getItem(AUTH_BOOTSTRAP_CACHE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return (
      parsed?.token === token &&
      Date.now() - Number(parsed.refreshedAt || 0) < AUTH_BOOTSTRAP_CACHE_TTL_MS
    );
  } catch {
    return false;
  }
}

function persistOrganizations(organizations) {
  if (organizations?.length) {
    window.localStorage.setItem(STORAGE_KEYS.ORGANIZATIONS, JSON.stringify(organizations));
  } else {
    window.localStorage.removeItem(STORAGE_KEYS.ORGANIZATIONS);
  }
}

function readCachedUser() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEYS.USER);
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed) return null;
    return normalizePlatformAdminFlags(mapUserFromApi(parsed));
  } catch {
    return null;
  }
}

function readCachedOrganizations() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEYS.ORGANIZATIONS);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function persistToken(token) {
  if (token) window.localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
  else window.localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
}

function persistCurrentOrgId(id) {
  if (id != null && id !== '') {
    window.localStorage.setItem(STORAGE_KEYS.CURRENT_ORG_ID, String(id));
  } else {
    window.localStorage.removeItem(STORAGE_KEYS.CURRENT_ORG_ID);
  }
}

function readCurrentOrgId() {
  return window.localStorage.getItem(STORAGE_KEYS.CURRENT_ORG_ID);
}

function readToken() {
  return window.localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
}

function readAdminToken() {
  return window.localStorage.getItem(STORAGE_KEYS.ADMIN_AUTH_TOKEN);
}

function persistAdminToken(token) {
  if (token) window.localStorage.setItem(STORAGE_KEYS.ADMIN_AUTH_TOKEN, token);
  else window.localStorage.removeItem(STORAGE_KEYS.ADMIN_AUTH_TOKEN);
}

function persistAdminUser(user) {
  if (user) window.localStorage.setItem(STORAGE_KEYS.ADMIN_USER, JSON.stringify(user));
  else window.localStorage.removeItem(STORAGE_KEYS.ADMIN_USER);
}

function readAdminUserSnapshot() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEYS.ADMIN_USER);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * انتخاب سازمان فعال از لیست با اولویت شناسهٔ ترجیحی (از API یا حافظهٔ محلی).
 */
function resolveActiveOrganization(organizations, preferredId, { allowResolve = true } = {}) {
  if (!allowResolve || !organizations?.length) return null;

  const cid =
    preferredId != null && preferredId !== '' ? preferredId : readCurrentOrgId();

  if (cid) {
    const chosen = organizations.find((o) => String(o.id) === String(cid));
    if (chosen?.id) {
      persistCurrentOrgId(chosen.id);
      return chosen;
    }
  }

  if (organizations.length === 1) {
    persistCurrentOrgId(organizations[0].id);
    return organizations[0];
  }

  return null;
}

function userNeedsTenant(user) {
  return requiresActiveOrganization(user);
}

function getInitialAuthState() {
  const token = readToken();
  if (!token) {
    return {
      user: null,
      organizations: [],
      activeOrganization: null,
      loading: false,
    };
  }

  const cachedUser = readCachedUser();
  const cachedOrgs = readCachedOrganizations();
  const active = userNeedsTenant(cachedUser)
    ? resolveActiveOrganization(cachedOrgs, readCurrentOrgId())
    : null;

  return {
    user: cachedUser,
    organizations: cachedOrgs,
    activeOrganization: active,
    /** فقط وقتی توکن داریم ولی کش کاربر نداریم، UI را مسدود کن */
    loading: !cachedUser,
  };
}

export const AuthProvider = ({ children }) => {
  const initial = useMemo(() => getInitialAuthState(), []);
  const [user, setUser] = useState(initial.user);
  const [organizations, setOrganizations] = useState(initial.organizations);
  const [activeOrganization, setActiveOrganization] = useState(initial.activeOrganization);
  const [loading, setLoading] = useState(initial.loading);
  const [sessionRefreshing, setSessionRefreshing] = useState(
    Boolean(readToken() && initial.user),
  );
  const [switchingOrg, setSwitchingOrg] = useState(false);
  const [orgContextKey, setOrgContextKey] = useState(() =>
    buildOrgContextKey(initial.user, initial.activeOrganization),
  );
  const [isImpersonating, setIsImpersonating] = useState(() => Boolean(readAdminToken()));
  const [impersonationLockedOrganizationId, setImpersonationLockedOrganizationId] = useState(null);
  const [stoppingImpersonation, setStoppingImpersonation] = useState(false);
  const bootstrapAbortRef = useRef(null);
  const userRef = useRef(user);
  const organizationsRef = useRef(organizations);
  const activeOrganizationRef = useRef(activeOrganization);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    organizationsRef.current = organizations;
  }, [organizations]);

  useEffect(() => {
    activeOrganizationRef.current = activeOrganization;
  }, [activeOrganization]);

  useEffect(() => installSiteSyncMessageListener(), []);

  const clearSession = useCallback(() => {
    persistToken(null);
    persistAdminToken(null);
    persistAdminUser(null);
    persistUser(null);
    persistOrganizations([]);
    persistCurrentOrgId(null);
    persistBootstrapRefresh(null);
    setUser(null);
    setOrganizations([]);
    setActiveOrganization(null);
    setOrgContextKey(':');
    setIsImpersonating(false);
    setImpersonationLockedOrganizationId(null);
    void clearSiteAuthSession();
  }, []);

  const applySession = useCallback((mapped, orgs, preferredOrgId) => {
    const normalized = normalizePlatformAdminFlags(mapped);
    const needsTenant = userNeedsTenant(normalized);
    const active = needsTenant
      ? resolveActiveOrganization(orgs, preferredOrgId ?? readCurrentOrgId())
      : null;
    if (!needsTenant) persistCurrentOrgId(null);
    setUser(normalized);
    setOrganizations(orgs);
    setActiveOrganization(active);
    setOrgContextKey(buildOrgContextKey(normalized, active));
    persistUser(normalized);
    persistOrganizations(orgs);
    persistBootstrapRefresh(readToken());
    void syncSiteAuthSession();
  }, []);

  /** پس از login / otp/verify — بدون فراخوانی مجددِ کامل bootstrap */
  const applyAuthResponseBody = useCallback(async (apiBody) => {
    const parsed = authApi.parseLoginPayload(apiBody);
    if (!parsed?.token) {
      throw new Error('توکن در پاسخ سرور یافت نشد.');
    }
    persistToken(parsed.token);
    let mapped = mapUserFromApi(parsed.user);
    mapped = mergeAuthUserPayload(mapped, {
      is_platform_super_admin: parsed.is_platform_super_admin ?? parsed.user?.is_platform_super_admin,
      requires_active_organization: parsed.requires_active_organization,
      permissions: parsed.permissions,
      platform_roles: parsed.platform_roles,
      organization_permissions: parsed.organization_permissions,
      organization_access_scopes: parsed.organization_access_scopes,
    });
    mapped = await enrichUserPlatformAccess(mapped);
    mapped = normalizePlatformAdminFlags(mapped);
    const noTenant = !userNeedsTenant(mapped);
    const preferredOrgId = noTenant ? null : parsed.current_organization_id;
    let orgs = parsed.organizations || [];
    if (noTenant) {
      persistCurrentOrgId(null);
    } else if (parsed.current_organization_id) {
      persistCurrentOrgId(parsed.current_organization_id);
    } else if (orgs.length > 1) {
      persistCurrentOrgId(null);
    }
    if (!orgs.length && !noTenant && !Boolean(readAdminToken())) {
      orgs = await authApi.fetchOrganizations();
    } else if (!orgs.length && noTenant) {
      try {
        const res = await orgApi.listOrganizations();
        orgs = res.items || [];
      } catch {
        orgs = [];
      }
    }
    applySession(mapped, orgs, preferredOrgId);
  }, [applySession]);

  const bootstrapSession = useCallback(
    async (signal) => {
      const token = readToken();
      if (!token) {
        clearSession();
        return;
      }

      const impersonating = Boolean(readAdminToken());
      const cachedSnapshot = impersonating ? null : readCachedUser();
      if ((impersonating || userNeedsTenant(cachedSnapshot)) && !readCurrentOrgId()) {
        resolveActiveOrganization(readCachedOrganizations(), null);
      }

      const requestOpts = { timeout: AUTH_BOOTSTRAP_TIMEOUT_MS, signal };
      const meRequestOpts = { ...requestOpts };
      const scopedOrgId = readCurrentOrgId();
      if (scopedOrgId && (impersonating || userNeedsTenant(cachedSnapshot))) {
        meRequestOpts.headers = { 'X-Organization-ID': String(scopedOrgId) };
      }

      const me = await authApi.fetchMePayload(meRequestOpts);
      let orgs = me.organizations;

      let mapped = mapUserFromApi(me.user);
      mapped = mergeAuthUserPayload(mapped, {
        permissions: me.permissions,
        platform_roles: me.platform_roles,
        requires_active_organization: me.requires_active_organization,
        is_platform_super_admin: me.is_platform_super_admin ?? me.user?.is_platform_super_admin,
        organization_permissions: me.organization_permissions,
        organization_access_scopes: me.organization_access_scopes,
      });
      mapped = await enrichUserPlatformAccess(mapped);
      if (!impersonating) {
        mapped = reconcilePlatformAdminProfile(mapped, cachedSnapshot);
      }
      const noTenant = !userNeedsTenant(mapped);

      if (!orgs.length && !noTenant) {
        orgs = await authApi.fetchOrganizations(requestOpts);
      } else if (!orgs.length && noTenant && !impersonating) {
        try {
          const res = await orgApi.listOrganizations({}, requestOpts);
          orgs = res.items || [];
        } catch {
          orgs = [];
        }
      }

      let preferredOrgId = noTenant
        ? null
        : me.impersonation_locked_organization_id
          ?? me.active_organization_id
          ?? me.current_organization_id
          ?? readCurrentOrgId();
      if (!preferredOrgId && !noTenant && orgs.length === 1 && orgs[0]?.id) {
        preferredOrgId = orgs[0].id;
      }
      if (noTenant) persistCurrentOrgId(null);
      if (me.impersonation_locked_organization_id) {
        persistCurrentOrgId(me.impersonation_locked_organization_id);
        setImpersonationLockedOrganizationId(me.impersonation_locked_organization_id);
      } else {
        setImpersonationLockedOrganizationId(null);
      }

      let sessionUser = mapped;
      const orgIdForPermissions =
        me.impersonation_locked_organization_id ?? preferredOrgId;
      if (
        !noTenant &&
        orgIdForPermissions &&
        !mapped.organization_permissions?.length
      ) {
        persistCurrentOrgId(orgIdForPermissions);
        const refreshedMe = await authApi.fetchMePayload({
          ...requestOpts,
          headers: { 'X-Organization-ID': String(orgIdForPermissions) },
        });
        sessionUser = mapUserFromApi(refreshedMe.user);
        sessionUser = mergeAuthUserPayload(sessionUser, {
          permissions: refreshedMe.permissions,
          platform_roles: refreshedMe.platform_roles,
          requires_active_organization: refreshedMe.requires_active_organization,
          is_platform_super_admin:
            refreshedMe.is_platform_super_admin ?? refreshedMe.user?.is_platform_super_admin,
          organization_permissions: refreshedMe.organization_permissions,
        });
        sessionUser = await enrichUserPlatformAccess(sessionUser);
        sessionUser = normalizePlatformAdminFlags(sessionUser);
        if (refreshedMe.organizations?.length) {
          orgs = refreshedMe.organizations;
        }
      }

      applySession(sessionUser, orgs, preferredOrgId);
    },
    [applySession, clearSession],
  );

  useEffect(() => {
    bootstrapAbortRef.current?.abort();
    const controller = new AbortController();
    bootstrapAbortRef.current = controller;

    let cancelled = false;

    (async () => {
      const token = readToken();
      if (!token) {
        if (!cancelled) {
          clearSession();
          setLoading(false);
          setSessionRefreshing(false);
        }
        return;
      }

      const cachedUser = readCachedUser();
      const hasCache = Boolean(cachedUser);
      const needsOrgPermissionRefresh =
        userNeedsTenant(cachedUser)
        && Boolean(readCurrentOrgId())
        && !cachedUser?.organization_permissions?.length;
      if (hasCache) {
        setLoading(false);
        setSessionRefreshing(true);
        if (!readAdminToken() && hasFreshBootstrapRefresh(token) && !needsOrgPermissionRefresh) {
          void syncSiteAuthSession();
          setSessionRefreshing(false);
          return;
        }
      } else {
        setLoading(true);
      }

      try {
        await bootstrapSession(controller.signal);
      } catch (e) {
        if (controller.signal.aborted || cancelled) return;
        if (e?.code === 'ERR_CANCELED') return;
        clearSession();
      } finally {
        if (!cancelled) {
          setLoading(false);
          setSessionRefreshing(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [bootstrapSession, clearSession]);

  const login = useCallback(
    async ({ identifier, password, organizationId, captcha }) => {
      const body = await authApi.loginWithPassword(identifier, password, organizationId, captcha);
      const parsed = authApi.parseLoginPayload(body);
      if (parsed?.requires_two_factor) return parsed;
      await applyAuthResponseBody(body);
      return parsed;
    },
    [applyAuthResponseBody],
  );

  const loginWithOtp = useCallback(
    async ({ identifier, otp, organizationId }) => {
      const body = await authApi.verifyOtp(identifier, otp, organizationId);
      const parsed = authApi.parseLoginPayload(body);
      if (parsed?.requires_two_factor) return parsed;
      await applyAuthResponseBody(body);
      return parsed;
    },
    [applyAuthResponseBody],
  );

  const completeTwoFactorLogin = useCallback(
    async ({ challengeToken, code }) => {
      const body = await authApi.completeTwoFactorLogin(challengeToken, code);
      await applyAuthResponseBody(body);
      return authApi.parseLoginPayload(body);
    },
    [applyAuthResponseBody],
  );

  const sendLoginOtp = useCallback(async (identifier, captcha) => {
    return authApi.requestOtp(identifier, captcha);
  }, []);

  const register = useCallback(
    async ({ name, mobile, password, otp, email, captcha }) => {
      const body = await authApi.registerAccount({ name, mobile, password, otp, email, captcha });
      const parsed = authApi.parseLoginPayload(body);
      await applyAuthResponseBody(body);
      return parsed;
    },
    [applyAuthResponseBody],
  );

  const sendRegisterOtp = useCallback(async (mobile, captcha) => {
    return authApi.requestRegisterOtp(mobile, captcha);
  }, []);

  const logout = useCallback(async () => {
    try {
      if (isImpersonating) {
        await authApi.stopImpersonation();
      } else {
        await authApi.logout();
      }
    } catch {
      /* ignore */
    } finally {
      clearSession();
    }
  }, [clearSession, isImpersonating]);

  /**
   * شروع impersonation — توکن فعلی به‌عنوان ادمین ذخیره و توکن جدید جایگزین می‌شود.
   * برای دکمه‌های «ورود به‌عنوان» در صفحات کاربر/عضو استفاده می‌شود.
   */
  const beginImpersonation = useCallback(
    async (impersonationToken, { organizationId } = {}) => {
      if (!impersonationToken || typeof impersonationToken !== 'string') {
        throw new Error('توکن impersonation معتبر نیست.');
      }
      const currentToken = readToken();
      if (currentToken) persistAdminToken(currentToken);
      const adminSnapshot = user || readCachedUser();
      if (adminSnapshot) persistAdminUser(adminSnapshot);
      persistUser(null);
      persistOrganizations([]);
      if (organizationId != null && organizationId !== '') {
        persistCurrentOrgId(organizationId);
      } else {
        persistCurrentOrgId(null);
      }
      persistToken(impersonationToken);
      setIsImpersonating(true);
      await bootstrapSession();
      return {
        user: userRef.current,
        organizations: organizationsRef.current,
        activeOrganization: activeOrganizationRef.current,
      };
    },
    [bootstrapSession, user],
  );

  /** پایان impersonation — `POST /auth/stop-impersonation` و بازگردانی توکن ادمین */
  const stopImpersonationSession = useCallback(async () => {
    setStoppingImpersonation(true);
    const adminToken = readAdminToken();
    const adminSnapshot = readAdminUserSnapshot();
    try {
      try {
        await authApi.stopImpersonation();
      } catch {
        /* حتی در خطا تلاش برای بازگردانی توکن ادمین */
      }
      persistAdminToken(null);
      persistAdminUser(null);
      setIsImpersonating(false);
      setImpersonationLockedOrganizationId(null);
      if (adminToken) {
        persistToken(adminToken);
        const requestOpts = { timeout: AUTH_BOOTSTRAP_TIMEOUT_MS };
        const me = await authApi.fetchMePayload(requestOpts);
        let orgs = me.organizations;
        let mapped = mapUserFromApi(me.user);
        mapped = mergeAuthUserPayload(mapped, {
          permissions: me.permissions,
          platform_roles: me.platform_roles,
          requires_active_organization: me.requires_active_organization,
          is_platform_super_admin: me.is_platform_super_admin ?? me.user?.is_platform_super_admin,
          organization_permissions: me.organization_permissions,
        organization_access_scopes: me.organization_access_scopes,
        });
        mapped = await enrichUserPlatformAccess(mapped);
        mapped = restorePlatformAdminProfile(mapped, adminSnapshot);
        mapped = normalizePlatformAdminFlags(mapped);
        const noTenant = !userNeedsTenant(mapped);
        if (!orgs.length && !noTenant) {
          orgs = await authApi.fetchOrganizations(requestOpts);
        } else if (!orgs.length && noTenant) {
          try {
            const res = await orgApi.listOrganizations({}, requestOpts);
            orgs = res.items || [];
          } catch {
            orgs = [];
          }
        }
        const preferredOrgId = noTenant
          ? null
          : me.impersonation_locked_organization_id
            ?? me.active_organization_id
            ?? me.current_organization_id
            ?? readCurrentOrgId();
        if (noTenant) persistCurrentOrgId(null);
        applySession(mapped, orgs, preferredOrgId);
        return mapped;
      }
      clearSession();
      throw new Error('توکن ادمین یافت نشد. لطفاً دوباره وارد شوید.');
    } finally {
      setStoppingImpersonation(false);
    }
  }, [applySession, clearSession]);

  const switchActiveOrganization = useCallback(
    async (organizationId) => {
      if (!userNeedsTenant(user)) {
        throw new Error('سوپرادمین سازمان فعال ندارد.');
      }
      if (
        impersonationLockedOrganizationId &&
        String(impersonationLockedOrganizationId) !== String(organizationId)
      ) {
        throw new Error('در حالت ورود به‌عنوان کاربر دیگر نمی‌توانید سازمان را تغییر دهید.');
      }
      setSwitchingOrg(true);
      try {
        await authApi.switchOrganization(organizationId);
        persistCurrentOrgId(organizationId);
        const me = await authApi.fetchMePayload({
          timeout: AUTH_BOOTSTRAP_TIMEOUT_MS,
          headers: { 'X-Organization-ID': String(organizationId) },
        });
        let orgs = me.organizations;
        if (!orgs.length) orgs = await authApi.fetchOrganizations({ timeout: AUTH_BOOTSTRAP_TIMEOUT_MS });
        let mapped = mapUserFromApi(me.user);
        mapped = mergeAuthUserPayload(mapped, {
          permissions: me.permissions,
          platform_roles: me.platform_roles,
          requires_active_organization: me.requires_active_organization,
          is_platform_super_admin: me.is_platform_super_admin ?? me.user?.is_platform_super_admin,
          organization_permissions: me.organization_permissions,
        organization_access_scopes: me.organization_access_scopes,
        });
        mapped = normalizePlatformAdminFlags(mapped);
        applySession(mapped, orgs, organizationId);
      } finally {
        setSwitchingOrg(false);
      }
    },
    [applySession, user, impersonationLockedOrganizationId],
  );

  /** همگام‌سازی state از پاسخ GET /profile — refها از حلقهٔ re-fetch در ProfilePage جلوگیری می‌کنند */
  const syncAuthFromProfile = useCallback(
    (profilePayload) => {
      if (!profilePayload || typeof profilePayload !== 'object') return;
      const noTenant =
        profilePayload.requires_active_organization === false ||
        profilePayload.is_platform_super_admin === true;
      const currentUser = userRef.current;
      const currentOrgs = organizationsRef.current;
      const currentActiveOrg = activeOrganizationRef.current;
      let mapped = currentUser ? { ...currentUser } : mapUserFromApi(profilePayload);
      mapped = mergeAuthUserPayload(mapped, {
        is_platform_super_admin: profilePayload.is_platform_super_admin,
        requires_active_organization: profilePayload.requires_active_organization,
      });
      mapped = normalizePlatformAdminFlags(mapped);
      if (
        Object.prototype.hasOwnProperty.call(profilePayload, 'avatar')
        || Object.prototype.hasOwnProperty.call(profilePayload, 'avatar_url')
      ) {
        mapped.avatar = userAvatarUrl(profilePayload);
      }
      const orgs = Array.isArray(profilePayload.organizations)
        ? mergeAuthOrganizations(currentOrgs, profilePayload.organizations)
        : currentOrgs;
      const preferredOrgId = noTenant ? null : currentActiveOrg?.id ?? readCurrentOrgId();
      if (noTenant) persistCurrentOrgId(null);
      applySession(mapped, orgs, preferredOrgId);
    },
    [applySession],
  );

  const patchAuthUser = useCallback((fields) => {
    if (!fields || typeof fields !== 'object') return;
    const current = userRef.current;
    if (!current) return;
    const next = { ...current, ...fields };
    userRef.current = next;
    setUser(next);
    persistUser(next);
  }, []);

  const patchOrganization = useCallback((organizationId, fields) => {
    if (organizationId == null || organizationId === '' || !fields || typeof fields !== 'object') return;
    const orgs = (organizationsRef.current || []).map((org) => (
      String(org?.id) === String(organizationId) ? { ...org, ...fields } : org
    ));
    organizationsRef.current = orgs;
    setOrganizations(orgs);
    persistOrganizations(orgs);
    const active = activeOrganizationRef.current;
    if (active && String(active.id) === String(organizationId)) {
      const nextActive = { ...active, ...fields };
      activeOrganizationRef.current = nextActive;
      setActiveOrganization(nextActive);
    }
  }, []);

  const platformSuperAdmin = isPlatformSuperAdmin(user);
  const needsActiveOrganization = requiresActiveOrganization(user);

  const value = useMemo(
    () => ({
      user,
      organizations,
      activeOrganization,
      isPlatformSuperAdmin: platformSuperAdmin,
      requiresActiveOrganization: needsActiveOrganization,
      isAuthenticated: !!user,
      loading,
      sessionRefreshing,
      switchingOrg,
      orgContextKey,
      isImpersonating,
      impersonationLockedOrganizationId,
      stoppingImpersonation,
      login,
      loginWithOtp,
      register,
      sendRegisterOtp,
      completeTwoFactorLogin,
      sendLoginOtp,
      logout,
      beginImpersonation,
      stopImpersonation: stopImpersonationSession,
      switchActiveOrganization,
      refreshSession: bootstrapSession,
      syncAuthFromProfile,
      patchAuthUser,
      patchOrganization,
    }),
    [
      user,
      organizations,
      activeOrganization,
      platformSuperAdmin,
      needsActiveOrganization,
      loading,
      sessionRefreshing,
      switchingOrg,
      orgContextKey,
      isImpersonating,
      impersonationLockedOrganizationId,
      stoppingImpersonation,
      login,
      loginWithOtp,
      register,
      sendRegisterOtp,
      completeTwoFactorLogin,
      sendLoginOtp,
      logout,
      beginImpersonation,
      stopImpersonationSession,
      switchActiveOrganization,
      bootstrapSession,
      syncAuthFromProfile,
      patchAuthUser,
      patchOrganization,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
